import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { CircleNotch, CaretDown, CaretUp, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { TenantSelect } from '@/components/platform/TenantSelect';
import {
  PLATFORM_CATEGORIES,
  PLATFORM_CATEGORY_LABEL,
  platformActionsInCategory,
  platformActionLabel,
  platformActionBadgeClass,
  type PlatformCategory,
} from '@/lib/platformAuditActions';

// ── Types ─────────────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  action: string;
  actorEmail: string;
  targetType?: string;
  targetId?: string;
  target?: { name: string; slug: string } | null;
  before?: unknown;
  after?: unknown;
  metadata?: unknown;
  ip?: string;
  userAgent?: string;
  createdAt: string;
}

interface AuditResponse {
  data: AuditEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// ── Helpers ───────────────────────────────────────────────────────────────

function formatDateTime(str: string) {
  return new Date(str).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * The action badge.
 *
 * Was a substring regex over the raw string, printing the token itself — so the console showed
 * `platform_admin.password_reset_out_of_band`, and `tenant.deactivate` would have coloured green
 * because it contains "activate". Both label and colour now come from the taxonomy, which is
 * keyed on the whole action; the raw value stays available on hover for debugging.
 */
function ActionBadge({ action }: { action: string }) {
  return (
    <span
      title={action}
      className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] ${platformActionBadgeClass(action)}`}
    >
      {platformActionLabel(action)}
    </span>
  );
}

function JsonPanel({ label, value }: { label: string; value: unknown }) {
  if (value === undefined || value === null) return null;
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-plat-muted">{label}</div>
      <pre className="text-xs overflow-auto rounded bg-plat-bg border border-plat-border p-3 text-plat-secondary max-h-48">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/** Shared control styling — the selects and inputs must line up in the toolbar. */
const SELECT_CLASS =
  'h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50 disabled:cursor-not-allowed disabled:text-plat-disabled';

// ── AuditLogPage ──────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const [category, setCategory] = useState<PlatformCategory | ''>('');
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);

  const filterParams = {
    action: actionFilter || undefined,
    category: category || undefined,
    actorEmail: actorFilter || undefined,
    targetType: tenantFilter ? ('chambers' as const) : undefined,
    targetId: tenantFilter || undefined,
    from: fromDate ? new Date(fromDate).toISOString() : undefined,
    // Inclusive end-of-day: a "To" of the 10th must include the 10th, not stop at midnight.
    to: toDate ? new Date(`${toDate}T23:59:59.999Z`).toISOString() : undefined,
  };

  const { data, isLoading, isFetching, error } = useQuery<AuditResponse>({
    queryKey: ['platform', 'audit', category, actionFilter, actorFilter, fromDate, toDate, tenantFilter, page],
    queryFn: async () => {
      const res = await platformApi.get('/audit', {
        params: { ...filterParams, page, limit: 50 },
      });
      // listAuditLog returns { data, pagination }; the controller sends it unwrapped.
      return res.data;
    },
    // Keep the current rows visible while the next query runs, rather than flashing the spinner.
    placeholderData: keepPreviousData,
  });

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await platformApi.get('/audit/export', {
        params: filterParams,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `platform-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1_000);

      const rowCount = Number(res.headers['x-row-count'] ?? 0);
      toast.success(
        res.headers['x-truncated'] === 'true'
          ? `Downloaded the first ${rowCount.toLocaleString()} rows — narrow the filter for the rest.`
          : `Downloaded ${rowCount.toLocaleString()} rows.`,
      );
    } catch (err) {
      toast.error(getPlatformApiErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  }

  function resetFilters() {
    setCategory(''); setActionFilter(''); setActorFilter('');
    setFromDate(''); setToDate(''); setTenantFilter(''); setPage(1);
  }

  const hasFilters = !!(category || actionFilter || actorFilter || fromDate || toDate || tenantFilter);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleFilterChange() {
    setPage(1);
  }

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Category → action. Replaces a free-text "Filter by action…" box, which required
            knowing the exact string (`tenant.plan_change`) before it could match anything. */}
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value as PlatformCategory | ''); setActionFilter(''); handleFilterChange(); }}
          className={SELECT_CLASS}
          aria-label="Activity category"
        >
          <option value="">All activity</option>
          {PLATFORM_CATEGORIES.map((c) => (
            <option key={c} value={c}>{PLATFORM_CATEGORY_LABEL[c]}</option>
          ))}
        </select>

        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); handleFilterChange(); }}
          className={SELECT_CLASS}
          disabled={!category}
          aria-label="Action"
          title={category ? undefined : 'Choose a category first'}
        >
          <option value="">{category ? 'All actions in category' : 'All actions'}</option>
          {category && platformActionsInCategory(category).map((a) => (
            <option key={a} value={a}>{platformActionLabel(a)}</option>
          ))}
        </select>

        <input
          value={actorFilter}
          onChange={(e) => { setActorFilter(e.target.value); handleFilterChange(); }}
          placeholder="Actor email…"
          className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-plat-muted">From</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); handleFilterChange(); }}
            className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-plat-muted">To</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); handleFilterChange(); }}
            className="h-9 rounded-lg border border-plat-border bg-plat-surface px-3 text-sm text-plat-fg focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50"
          />
        </div>
        <div className="w-56" title="Filter to events targeting a specific organisation">
          <TenantSelect
            value={tenantFilter}
            onChange={(v) => { setTenantFilter(v); handleFilterChange(); }}
            includeAll
            allLabel="All organisations"
          />
        </div>

        {hasFilters && (
          <button onClick={resetFilters} className="text-xs font-medium text-plat-info-text hover:underline">
            Clear filters
          </button>
        )}

        <button
          onClick={handleDownload}
          disabled={downloading || (data?.data ?? []).length === 0}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-plat-border bg-plat-surface px-3 py-2 text-xs font-medium text-plat-secondary transition hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
        >
          {downloading
            ? <CircleNotch className="h-3.5 w-3.5 animate-spin" />
            : <DownloadSimple className="h-3.5 w-3.5" />}
          Download CSV
        </button>
      </div>

      {/* Result count — the page previously gave no sense of scale until you reached the
          pagination row, which is hidden entirely when there is only one page. */}
      {data && (
        <div className="flex items-center gap-2 text-sm text-plat-secondary">
          <span>
            {hasFilters ? 'Filtered entries' : 'All entries'}
            <span className="ml-1.5 tabular-nums text-plat-muted">({data.pagination.total.toLocaleString()})</span>
          </span>
          {isFetching && !isLoading && <CircleNotch className="h-3.5 w-3.5 animate-spin text-plat-faint" />}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
          {getPlatformApiErrorMessage(error)}
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted w-44">Timestamp</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Actor</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Action</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Target</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">IP</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {(data?.data ?? []).map((entry) => {
                  const isOpen = expanded.has(entry.id);
                  const hasDetail = entry.before !== undefined || entry.after !== undefined || entry.metadata !== undefined;
                  return (
                    <>
                      <tr key={entry.id} className="transition hover:bg-plat-hover">
                        <td className="px-5 py-3 text-xs text-plat-muted whitespace-nowrap">{formatDateTime(entry.createdAt)}</td>
                        <td className="px-5 py-3 text-plat-secondary">{entry.actorEmail}</td>
                        <td className="px-5 py-3"><ActionBadge action={entry.action} /></td>
                        <td className="px-5 py-3">
                          {entry.target ? (
                            <div>
                              <div className="text-plat-neutral-text">{entry.target.name}</div>
                              <div className="text-[10px] text-plat-muted">{entry.target.slug}</div>
                            </div>
                          ) : entry.targetType ? (
                            <span className="text-plat-muted text-xs">{entry.targetType}</span>
                          ) : (
                            <span className="text-plat-faint">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-xs text-plat-muted">{entry.ip ?? '—'}</td>
                        <td className="px-5 py-3">
                          {hasDetail && (
                            <button
                              onClick={() => toggleExpand(entry.id)}
                              className="text-plat-muted hover:text-plat-secondary"
                              aria-label={isOpen ? 'Collapse' : 'Expand'}
                            >
                              {isOpen ? <CaretUp className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && hasDetail && (
                        <tr key={`${entry.id}-detail`}>
                          <td colSpan={6} className="bg-plat-elevated px-5 py-4">
                            <div className="space-y-3">
                              <JsonPanel label="Before" value={entry.before} />
                              <JsonPanel label="After" value={entry.after} />
                              <JsonPanel label="Metadata" value={entry.metadata} />
                              {entry.userAgent && (
                                <div>
                                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-plat-muted">User Agent</div>
                                  <div className="text-xs text-plat-muted break-all">{entry.userAgent}</div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {(data?.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm text-plat-muted">
                      No audit entries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-plat-muted">
                Page {data.pagination.page} of {data.pagination.totalPages} — {data.pagination.total} total
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Previous
                </button>
                <button
                  disabled={page >= data.pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-plat-border px-3 py-1.5 text-xs text-plat-secondary hover:border-plat-border-strong hover:text-plat-fg disabled:cursor-not-allowed disabled:text-plat-disabled"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
